import React, { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";
import CampanaSociedadesBanner from "@/components/CampanaSociedadesBanner";
import { api, formatEUR, formatNum, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
    CheckCircle, Warning, XCircle, Sparkle, ArrowRight, Lock, Lightning, ShieldCheck, PencilSimple, Lightbulb, Info, Printer, X,
} from "@phosphor-icons/react";

const EMPTY = {
    // Identificación obligatoria
    email: "",
    telefono: "",
    nombre: "",
    empresa: "",
    tipo_cliente: "empresa", // "empresa" | "asesoria"
    // Balance + P&G
    activo_no_corriente: "",
    activo_corriente: "",
    capital: "",
    reservas_remanente: "",
    resultado_ejercicio: "",
    pasivo_no_corriente: "",
    pasivo_corriente: "",
    ingresos_corrientes: "",
    variacion_obra: "",
    compras_aprovisionamientos: "",
    variacion_existencias: "",
    gastos_generales: "",
    coste_personal: "",
    amortizaciones: "",
    gastos_financieros: "",
    sector: "general",
};

const IDENTITY_FIELDS = ["email", "telefono", "nombre", "empresa", "tipo_cliente"];
const NUMERIC_FIELDS_KEYS = Object.keys(EMPTY).filter((k) => k !== "sector" && !IDENTITY_FIELDS.includes(k));

const SECTORS = [
    { v: "general", l: "General / Industria" },
    { v: "construccion", l: "Construcción / Obras" },
    { v: "servicios", l: "Servicios profesionales" },
    { v: "comercio", l: "Comercio / Distribución" },
    { v: "hosteleria", l: "Hostelería / Restauración" },
];

const COLORS = {
    verde: "var(--gf-success)",
    ambar: "var(--gf-warning)",
    rojo: "var(--gf-danger)",
    "n/a": "var(--gf-text-muted)",
};
const BADGE = {
    verde: "Saludable",
    ambar: "Atención",
    rojo: "Riesgo",
    "n/a": "—",
};

const DRAFT_KEY = "tm_draft_v1";

export default function TruthMachine() {
    const { user, refresh } = useAuth();
    const [searchParams] = useSearchParams();
    const isAdmin = user?.role === "admin";
    const [form, setForm] = useState(() => {
        // Restaurar borrador de localStorage al montar
        try {
            const raw = localStorage.getItem(DRAFT_KEY);
            if (raw) {
                const saved = JSON.parse(raw);
                if (saved && typeof saved === "object" && saved.form) {
                    return { ...EMPTY, ...saved.form };
                }
            }
        } catch { /* ignore */ }
        return EMPTY;
    });
    const [draftSavedAt, setDraftSavedAt] = useState(() => {
        try {
            const raw = localStorage.getItem(DRAFT_KEY);
            if (raw) {
                const saved = JSON.parse(raw);
                return saved?.saved_at || null;
            }
        } catch { /* ignore */ }
        return null;
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);
    const [previousResult, setPreviousResult] = useState(null); // para comparativa "Antes / Después"
    const [initialResult, setInitialResult] = useState(null); // baseline para barra de progreso
    const [appliedRecs, setAppliedRecs] = useState(new Set()); // ids de recomendaciones aplicadas
    const [blocked, setBlocked] = useState(false); // true si el backend respondió 402 (créditos insuficientes)
    const [reopening, setReopening] = useState(false);
    const [recharging, setRecharging] = useState(false);

    // Reabrir un dictamen ya pagado desde el historial (?dictamen=ID) — sin nuevo cobro
    useEffect(() => {
        const id = searchParams.get("dictamen");
        if (!id) return;
        let cancelled = false;
        setReopening(true);
        api.get(`/dictamenes/${id}`)
            .then(({ data }) => {
                if (cancelled) return;
                const f = { ...EMPTY, empresa: data.empresa || "", sector: data.form?.sector || "general" };
                NUMERIC_FIELDS_KEYS.forEach((k) => { const v = data.form?.[k]; f[k] = v ? String(v) : ""; });
                setForm(f);
                setResult({ ...data.result, dictamen_id: data.id, charged_eur: 0 });
                setInitialResult(data.result);
                setAppliedRecs(new Set());
                setPreviousResult(null);
            })
            .catch((err) => { if (!cancelled) setError(formatApiError(err, "No se pudo reabrir el dictamen")); })
            .finally(() => { if (!cancelled) setReopening(false); });
        return () => { cancelled = true; };
    }, [searchParams]);

    // Autoguardado cada 30s (solo si el usuario ha empezado a rellenar y no hay result ya generado)
    useEffect(() => {
        if (result) return; // no seguir guardando si ya se generó el informe
        const hasContent = Object.entries(form).some(([k, v]) => k !== "sector" && v !== "" && v !== 0 && v !== null && v !== undefined);
        if (!hasContent) return;
        const t = setInterval(() => {
            try {
                const now = new Date().toISOString();
                localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, saved_at: now }));
                setDraftSavedAt(now);
            } catch { /* ignore */ }
        }, 30000);
        return () => clearInterval(t);
    }, [form, result]);

    function clearDraft() {
        try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
        setDraftSavedAt(null);
        setForm(EMPTY);
    }

    function setField(k, v) {
        setForm((f) => ({ ...f, [k]: v }));
    }

    const totals = useMemo(() => {
        const n = (k) => Number(form[k]) || 0;
        const totalActivo = n("activo_no_corriente") + n("activo_corriente");
        const patrimonioNeto = n("capital") + n("reservas_remanente") + n("resultado_ejercicio");
        const totalPnPasivo = patrimonioNeto + n("pasivo_no_corriente") + n("pasivo_corriente");
        const diferencia = totalActivo - totalPnPasivo;
        const cuadra = totalActivo === 0 ? true : Math.abs(diferencia) < Math.max(1, totalActivo * 0.01);
        // Resultado de la Cuenta de Pérdidas y Ganancias (estimación pre-impuesto).
        // Se compara con el "Resultado del ejercicio" declarado en el Pasivo para detectar incoherencias.
        const resultadoPyg = n("ingresos_corrientes") + n("variacion_obra")
            - n("compras_aprovisionamientos") + n("variacion_existencias")
            - n("gastos_generales") - n("coste_personal")
            - n("amortizaciones") - n("gastos_financieros");
        const rdoDeclarado = n("resultado_ejercicio");
        const rdoDiferencia = resultadoPyg - rdoDeclarado;
        const rdoCuadra = resultadoPyg === 0 && rdoDeclarado === 0
            ? true
            : Math.abs(rdoDiferencia) < Math.max(1, Math.abs(resultadoPyg) * 0.05);
        return { totalActivo, totalPnPasivo, diferencia, cuadra, patrimonioNeto, resultadoPyg, rdoDeclarado, rdoDiferencia, rdoCuadra };
    }, [form]);

    function buildPayload(currentForm) {
        const payload = {
            sector: currentForm.sector,
            email: (currentForm.email || "").trim(),
            telefono: (currentForm.telefono || "").trim(),
            nombre: (currentForm.nombre || "").trim() || null,
            empresa: (currentForm.empresa || "").trim() || null,
            tipo_cliente: currentForm.tipo_cliente || "empresa",
        };
        NUMERIC_FIELDS_KEYS.forEach((k) => { payload[k] = Number(currentForm[k]) || 0; });
        return payload;
    }

    async function handleSubmit(e) {
        if (e?.preventDefault) e.preventDefault();
        setError("");
        const payload = buildPayload(form);
        const allZero = NUMERIC_FIELDS_KEYS.every((k) => payload[k] === 0);
        if (allZero) {
            setError("Introduce al menos las cifras del balance y los ingresos para que la Máquina pueda dictaminar.");
            return;
        }
        if (totals.totalActivo > 0 && !totals.cuadra) {
            if (!window.confirm("El balance no cuadra. ¿Quieres que la Máquina de la Verdad lo diagnostique igualmente? (te lo marcará como alerta roja crítica)")) {
                return;
            }
        }
        // Usuario siempre autenticado (ruta protegida). Se descuentan 14 créditos la 1ª vez.
        await submitToBackend(form);
    }

    function dictamenPayload(currentForm) {
        return { ...buildPayload(currentForm), dictamen_id: result?.dictamen_id || null };
    }

    async function submitToBackend(currentForm) {
        setError("");
        setLoading(true);
        try {
            const { data } = await api.post("/dictamen-prebancario", dictamenPayload(currentForm));
            if (result) {
                setPreviousResult(result);
            } else {
                setInitialResult(data);
                setAppliedRecs(new Set());
            }
            setResult(data);
            if (data?.charged_eur > 0 && refresh) refresh();
            try { localStorage.removeItem(DRAFT_KEY); setDraftSavedAt(null); } catch { /* ignore */ }
            setTimeout(() => {
                document.getElementById("resultado-maquina")?.scrollIntoView({ behavior: "smooth" });
            }, 200);
        } catch (err) {
            if (err?.response?.status === 402) {
                setBlocked(true);
                window.scrollTo({ top: 0, behavior: "smooth" });
            } else {
                setError(formatApiError(err, "Error al consultar la Máquina de la Verdad"));
            }
        } finally {
            setLoading(false);
        }
    }

    function reset() {
        setResult(null);
        setPreviousResult(null);
        setInitialResult(null);
        setAppliedRecs(new Set());
        setForm(EMPTY);
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    async function quickRecharge() {
        // Guarda el borrador al instante para no perder las cifras durante el pago en Stripe
        try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, saved_at: new Date().toISOString() })); } catch { /* ignore */ }
        setRecharging(true);
        setError("");
        try {
            const { data } = await api.post("/payments/checkout", { plan_id: "recarga_dictamen", origin_url: window.location.origin, next_path: "/maquina-verdad" });
            window.location.href = data.url;
        } catch (err) {
            setError(formatApiError(err, "No se pudo iniciar la recarga rápida"));
            setRecharging(false);
        }
    }

    function editData() {
        // Vuelve al formulario manteniendo los datos para que el usuario los modifique
        setResult(null);
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    async function applyRecommendation(ajusteProposto, recId) {
        // Aplica los ajustes propuestos por una recomendación y vuelve a lanzar la Máquina automáticamente
        // Solo disponible para admin — el público tiene la pantalla bloqueada
        setError("");
        const adjustedForm = { ...form };
        Object.entries(ajusteProposto).forEach(([k, v]) => {
            adjustedForm[k] = String(v);
        });
        setForm(adjustedForm);
        setLoading(true);
        try {
            const { data } = await api.post("/dictamen-prebancario", dictamenPayload(adjustedForm));
            if (result) setPreviousResult(result);
            setResult(data);
            if (recId) setAppliedRecs((s) => new Set([...s, recId]));
            setTimeout(() => {
                document.getElementById("resultado-maquina")?.scrollIntoView({ behavior: "smooth" });
            }, 200);
        } catch (err) {
            setError(formatApiError(err, "Error al aplicar la recomendación"));
        } finally {
            setLoading(false);
        }
    }

    async function applyAllRecommendations() {
        // Combo: aplica todas las recomendaciones del resultado actual de una sola vez
        if (!result?.recomendaciones?.length) return;
        setError("");
        const adjustedForm = { ...form };
        const ids = [];
        result.recomendaciones.forEach((rec) => {
            Object.entries(rec.ajuste_propuesto).forEach(([k, v]) => {
                adjustedForm[k] = String(v);
            });
            ids.push(rec.id);
        });
        setForm(adjustedForm);
        setLoading(true);
        try {
            const { data } = await api.post("/dictamen-prebancario", dictamenPayload(adjustedForm));
            setPreviousResult(result);
            setResult(data);
            setAppliedRecs((s) => new Set([...s, ...ids]));
            setTimeout(() => {
                document.getElementById("resultado-maquina")?.scrollIntoView({ behavior: "smooth" });
            }, 200);
        } catch (err) {
            setError(formatApiError(err, "Error al aplicar las recomendaciones"));
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="min-h-screen flex flex-col bg-[var(--gf-cream)]/40 print:bg-white">
            <div className="print:hidden">
                <Navbar />
                <CampanaSociedadesBanner variant="compact" />
            </div>
            <main className="flex-1">
                {/* Hero */}
                <section className="bg-[var(--gf-navy)] text-white py-16 md:py-20 print:hidden">
                    <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[var(--gf-gold)]/15 border border-[var(--gf-gold)]/40 mb-6">
                            <Lightning size={14} weight="fill" className="text-[var(--gf-gold)]" />
                            <span className="text-[10px] tracking-[0.3em] uppercase text-[var(--gf-gold)] font-semibold">Dictamen Pre-Bancario · 14 créditos</span>
                        </div>
                        <h1 className="serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] mb-5">
                            La Máquina<br />
                            <span className="italic text-[var(--gf-gold)]">de la Verdad</span>
                        </h1>
                        <p className="text-base md:text-lg text-white/75 max-w-2xl mx-auto leading-relaxed">
                            Introduce tu balance + cuenta de resultados y descubre, en
                            <strong className="text-white"> 60 segundos</strong>, si tu empresa pasaría el filtro del analista bancario.
                            <span className="block mt-1 text-sm text-[var(--gf-gold)]/90 italic">Más de 30 años de experiencia en banca empresarial · 14 créditos por dictamen · recálculos incluidos</span>
                        </p>
                    </div>
                </section>

                {/* Form & Result */}
                <section className="max-w-[1600px] mx-auto px-4 md:px-8 py-12 md:py-16">
                    {/* Banner de error PROMINENTE (arriba, fondo rojo, ancho completo) */}
                    {error && !blocked && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-6 border-l-4 border-l-[var(--gf-danger)] bg-red-50 p-5 flex items-start gap-4"
                            data-testid="tm-error-banner"
                        >
                            <XCircle size={24} weight="fill" className="text-[var(--gf-danger)] flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <div className="font-semibold text-[var(--gf-danger)] text-sm md:text-base mb-1">
                                    No se pudo generar el diagnóstico
                                </div>
                                <div className="text-sm text-red-800 leading-relaxed">{error}</div>
                            </div>
                            <button
                                onClick={() => setError("")}
                                className="text-red-600 hover:text-red-800 flex-shrink-0"
                                data-testid="tm-error-dismiss"
                                aria-label="Cerrar aviso"
                            >
                                <X size={18} />
                            </button>
                        </motion.div>
                    )}

                    <AnimatePresence mode="wait">
                        {reopening ? (
                            <div key="reopening" className="text-center py-20 text-[var(--gf-text-secondary)] flex items-center justify-center gap-3" data-testid="tm-reopening">
                                <span className="elegant-loader"><span /><span /><span /></span> Cargando tu dictamen…
                            </div>
                        ) : blocked ? (
                            <BlockedView key="blocked" onLogin={() => { setBlocked(false); window.scrollTo({ top: 0, behavior: "smooth" }); }} onQuickRecharge={quickRecharge} recharging={recharging} user={user} />
                        ) : !result ? (
                            <>
                                {/* Indicador borrador autoguardado */}
                                {draftSavedAt && (
                                    <div className="mb-3 flex items-center justify-between gap-3 text-xs text-[var(--gf-text-secondary)] bg-[var(--gf-cream)]/60 border border-[var(--gf-border)] px-3 py-2" data-testid="draft-indicator">
                                        <span className="inline-flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                                            Borrador guardado automáticamente ({new Date(draftSavedAt).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}). Puedes cerrar la pestaña sin miedo.
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (confirm("¿Descartar el borrador guardado y empezar de cero?")) clearDraft();
                                            }}
                                            className="text-[var(--gf-text-muted)] hover:text-[var(--gf-danger)] transition-colors uppercase tracking-widest text-[10px]"
                                            data-testid="draft-discard"
                                        >
                                            Descartar
                                        </button>
                                    </div>
                                )}
                                <motion.form
                                    key="form"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    onSubmit={handleSubmit}
                                    className="grid lg:grid-cols-12 gap-4"
                                    data-testid="truth-form"
                                >
                                {/* COL 1 (5/12): Balance */}
                                <div className="lg:col-span-5 space-y-6">
                                    {/* BALANCE */}
                                    <div className="gf-card-ficha">
                                        <div className="flex items-center justify-between mb-4">
                                            <div>
                                                <span className="micro-label">— Paso 1</span>
                                                <h2 className="serif text-xl mt-1">Balance de situación</h2>
                                                <p className="text-[11px] text-[var(--gf-text-secondary)] mt-1">Activo + PN desglosado + Pasivo</p>
                                            </div>
                                            <span className="text-[10px] tracking-widest uppercase text-[var(--gf-gold)]">€</span>
                                        </div>

                                        <div className="mb-3">
                                            <label className="gf-label">Empresa <span className="text-[var(--gf-text-muted)] font-normal">(opcional · para identificar el dictamen en tu historial)</span></label>
                                            <input
                                                type="text"
                                                className="gf-input"
                                                value={form.empresa}
                                                onChange={(e) => setField("empresa", e.target.value)}
                                                placeholder="Ej: Acme Mediterráneo S.L."
                                                data-testid="tm-empresa"
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-3 mb-3">
                                            <div className="text-center pb-2 border-b border-[var(--gf-gold)]">
                                                <span className="text-[9px] tracking-[0.25em] uppercase text-[var(--gf-gold)] font-semibold">Activo</span>
                                            </div>
                                            <div className="text-center pb-2 border-b border-[var(--gf-navy)]">
                                                <span className="text-[9px] tracking-[0.25em] uppercase text-[var(--gf-navy)] font-semibold">PN + Pasivo</span>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="space-y-2.5">
                                                <Field label="Activo no corriente" value={form.activo_no_corriente} onChange={(v) => setField("activo_no_corriente", v)} testId="tm-anc" hint="Inmovilizado material, intangible, inversiones LP" example="Nave + maquinaria + furgoneta = 180.000 €" />
                                                <Field label="Activo corriente" value={form.activo_corriente} onChange={(v) => setField("activo_corriente", v)} testId="tm-ac" hint="Existencias, deudores, tesorería" example="Stock 40.000 + clientes 25.000 + banco 15.000 = 80.000 €" />
                                            </div>
                                            <div className="space-y-2.5">
                                                {/* Patrimonio Neto desglosado */}
                                                <div className="border border-[var(--gf-navy)]/30 bg-[var(--gf-cream)]/40 p-2.5 -m-px">
                                                    <div className="text-[9px] tracking-[0.25em] uppercase text-[var(--gf-navy)] font-semibold mb-2">— Patrimonio neto</div>
                                                    <div className="space-y-2">
                                                        <Field label="Capital" value={form.capital} onChange={(v) => setField("capital", v)} testId="tm-capital" hint="Capital social + prima emisión + aportaciones socios" example="Capital social escritura: 3.000 €" />
                                                        <Field label="Reservas y remanente" value={form.reservas_remanente} onChange={(v) => setField("reservas_remanente", v)} testId="tm-reservas" hint="Reservas legales + voluntarias + remanente. Puede ser negativo si hay pérdidas acumuladas" example="Beneficios de ejercicios anteriores no repartidos: 45.000 €" />
                                                        <Field label="Resultado del ejercicio" value={form.resultado_ejercicio} onChange={(v) => setField("resultado_ejercicio", v)} testId="tm-rdo-ej" hint="Resultado del ejercicio actual (puede ser negativo si hay pérdidas)" example="Beneficio después de impuestos año actual: 12.000 €. Si perdiste dinero pon negativo: −8.000" />
                                                    </div>
                                                    <div className="mt-2 pt-2 border-t border-[var(--gf-navy)]/20 flex items-center justify-between">
                                                        <span className="text-[9px] tracking-widest uppercase text-[var(--gf-text-secondary)]">PN total</span>
                                                        <span className="mono font-semibold text-xs text-[var(--gf-navy)]" data-testid="tm-pn-subtotal">{formatEUR(totals.patrimonioNeto || 0)}</span>
                                                    </div>
                                                </div>
                                                <Field label="Pasivo no corriente" value={form.pasivo_no_corriente} onChange={(v) => setField("pasivo_no_corriente", v)} testId="tm-pnc" hint="Deudas a largo plazo (>1 año)" example="Préstamo bancario pendiente + leasing largo plazo = 90.000 €" />
                                                <Field label="Pasivo corriente" value={form.pasivo_corriente} onChange={(v) => setField("pasivo_corriente", v)} testId="tm-pc" hint="Deudas a corto plazo (≤1 año)" example="Proveedores 20.000 + Hacienda 5.000 + póliza crédito 15.000 = 40.000 €" />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-[var(--gf-border)]">
                                            <MiniTotal label="Total Activo" value={totals.totalActivo} accent="gold" />
                                            <MiniTotal label="Total PN + Pasivo" value={totals.totalPnPasivo} accent="navy" />
                                        </div>

                                        {totals.totalActivo > 0 && !totals.cuadra && (
                                            <div className="mt-3 text-xs text-[var(--gf-danger)] border-l-2 border-[var(--gf-danger)] pl-3 py-2 bg-red-50" data-testid="tm-no-cuadra">
                                                ⚠️ El balance no cuadra. Diferencia: {formatEUR(totals.diferencia)}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* COL 2 (4/12): Cuenta P&G */}
                                <div className="lg:col-span-4">
                                    <div className="gf-card-ficha">
                                        <div className="mb-4">
                                            <span className="micro-label">— Paso 2</span>
                                            <h2 className="serif text-xl mt-1">Cuenta de Pérdidas y Ganancias</h2>
                                            <p className="text-[11px] text-[var(--gf-text-secondary)] mt-1">
                                                Las 8 magnitudes clave para el analista bancario.
                                            </p>
                                        </div>

                                        <div className="mb-3">
                                            <label className="text-[10px] tracking-wider uppercase text-[var(--gf-text-secondary)] mb-1 block">Sector de actividad</label>
                                            <select
                                                className="gf-input text-xs py-2"
                                                value={form.sector}
                                                onChange={(e) => setField("sector", e.target.value)}
                                                data-testid="tm-sector"
                                            >
                                                {SECTORS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                                            </select>
                                        </div>

                                        <div className="space-y-2.5">
                                            <Field label="Ingresos corrientes" value={form.ingresos_corrientes} onChange={(v) => setField("ingresos_corrientes", v)} testId="tm-ingresos" hint="Importe neto de la cifra de negocios" example="Ventas facturadas año 2024: 480.000 €" />
                                            <Field label="Variación obra en curso y finalizada" value={form.variacion_obra} onChange={(v) => setField("variacion_obra", v)} testId="tm-obra" hint="Trabajos realizados por la empresa para su activo o pendientes de certificar" example="Obra iniciada no facturada: 12.000 €. Si no aplica: 0" />
                                            <Field label="Compras de mercaderías y aprovisionamientos" value={form.compras_aprovisionamientos} onChange={(v) => setField("compras_aprovisionamientos", v)} testId="tm-compras" hint="Compras + subcontratas + otros aprovisionamientos" example="Materia prima 150.000 + subcontratistas 30.000 = 180.000 €" />
                                            <Field label="Variación de existencias" value={form.variacion_existencias} onChange={(v) => setField("variacion_existencias", v)} testId="tm-var-exist" hint="(Existencias finales − iniciales). Si aumentaron, valor positivo" example="Stock final 40.000 − stock inicial 35.000 = 5.000 €" />
                                            <Field label="Gastos generales" value={form.gastos_generales} onChange={(v) => setField("gastos_generales", v)} testId="tm-gastos-gen" hint="Suministros, alquileres, servicios exteriores, etc." example="Alquiler 12.000 + luz 4.000 + gestoría 3.000 + otros 15.000 = 34.000 €" />
                                            <Field label="Coste de personal y Seguridad Social" value={form.coste_personal} onChange={(v) => setField("coste_personal", v)} testId="tm-personal" hint="Salarios + S.S. a cargo de la empresa" example="Salarios brutos 90.000 + S.S. empresa 27.000 = 117.000 €" />
                                            <Field label="Amortizaciones" value={form.amortizaciones} onChange={(v) => setField("amortizaciones", v)} testId="tm-amort" hint="Dotación a la amortización del ejercicio" example="10% de tu inmovilizado. Con 180.000 de activo: aprox 18.000 €" />
                                            <Field label="Gastos financieros" value={form.gastos_financieros} onChange={(v) => setField("gastos_financieros", v)} testId="tm-financieros" hint="Intereses pagados" example="Intereses del préstamo bancario en el año: 3.500 €" />
                                        </div>

                                        {/* Resultado calculado de la P&G vs. Resultado declarado en el Pasivo */}
                                        <div className="mt-4 pt-3 border-t border-[var(--gf-border)] space-y-2">
                                            <MiniTotal
                                                label="Resultado P&G (calculado)"
                                                value={totals.resultadoPyg}
                                                accent={totals.rdoCuadra ? "emerald" : "rose"}
                                            />
                                            <div className="flex items-center justify-between text-[11px] px-1">
                                                <span className="uppercase tracking-widest text-[var(--gf-text-muted)]">Rdo. ejercicio declarado en Pasivo</span>
                                                <span className="mono font-semibold text-[var(--gf-navy)]" data-testid="tm-rdo-declarado">{formatEUR(totals.rdoDeclarado)}</span>
                                            </div>
                                            {(totals.resultadoPyg !== 0 || totals.rdoDeclarado !== 0) && (
                                                <div className={`text-[11px] border-l-2 pl-3 py-1.5 ${totals.rdoCuadra ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-rose-600 bg-rose-50 text-rose-800"}`} data-testid="tm-rdo-cuadra">
                                                    {totals.rdoCuadra
                                                        ? "✅ El resultado de la P&G coincide con el declarado en el Pasivo."
                                                        : `⚠️ Diferencia entre ambos: ${formatEUR(totals.rdoDiferencia)}. Revisa impuesto sobre beneficios, ingresos/gastos financieros no incluidos u otros ajustes.`}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* COL 3 (3/12): Cuadro azul submit */}
                                <div className="lg:col-span-3 space-y-4">
                                    <div className="gf-card sticky top-24" style={{ background: "var(--gf-navy)", color: "white" }}>
                                        <div className="flex items-center gap-3 mb-4">
                                            <Lightning size={24} weight="fill" className="text-[var(--gf-gold)]" />
                                            <h3 className="serif text-xl">¿Listo?</h3>
                                        </div>
                                        <p className="text-sm text-white/75 leading-relaxed mb-5">
                                            La Máquina aplica las <strong className="text-[var(--gf-gold)]">7 reglas profesionales</strong> que usa
                                            un analista bancario antes de aprobar o denegar un expediente.
                                        </p>

                                        <div className="space-y-2 mb-5">
                                            <Bullet icon="✓">Diagnóstico en 9 ratios clave</Bullet>
                                            <Bullet icon="✓">EBITDA · EBIT · cobertura intereses</Bullet>
                                            <Bullet icon="✓">Veredicto verde / ámbar / rojo</Bullet>
                                            <Bullet icon="✓">Alertas críticas detectadas</Bullet>
                                        </div>

                                        {error && (
                                            <div className="text-xs text-[var(--gf-danger)] border-l-2 border-[var(--gf-danger)] pl-3 py-2 bg-red-50 mb-4" data-testid="tm-error">
                                                {error}
                                            </div>
                                        )}

                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="btn-gold w-full justify-center"
                                            data-testid="tm-submit"
                                        >
                                            {loading ? (
                                                <><span className="elegant-loader"><span /><span /><span /></span> &nbsp; Diagnosticando…</>
                                            ) : (
                                                <>Encender la Máquina <ArrowRight size={16} /></>
                                            )}
                                        </button>

                                        {!isAdmin && Number(user?.credits_eur || 0) < 14 && (
                                            <div className="mt-3 border-l-2 border-amber-400 bg-amber-50 text-amber-900 text-xs px-3 py-2.5" data-testid="tm-low-credits-notice">
                                                <strong>Saldo insuficiente:</strong> tienes {formatEUR(user?.credits_eur || 0)} y el dictamen cuesta 14 créditos.
                                                <button
                                                    type="button"
                                                    onClick={quickRecharge}
                                                    disabled={recharging}
                                                    className="mt-2 w-full bg-[var(--gf-gold)] hover:bg-[#b39568] text-white text-xs font-semibold py-2 px-3 inline-flex items-center justify-center gap-1.5 transition-colors disabled:opacity-60"
                                                    data-testid="tm-quick-recharge"
                                                >
                                                    <Lightning size={12} weight="fill" /> {recharging ? "Abriendo pago seguro…" : "Recargar 14 créditos · 16,94 € IVA incl."}
                                                </button>
                                                <Link to="/pagos" className="block text-center mt-1.5 underline underline-offset-2 text-[11px] text-amber-800 hover:text-amber-950" data-testid="tm-low-credits-more">
                                                    o elegir otra recarga
                                                </Link>
                                            </div>
                                        )}

                                        <div className="text-[10px] text-center text-white/50 flex items-center justify-center gap-1 pt-3" data-testid="tm-price-note">
                                            <Lock size={10} /> {isAdmin ? "Admin · sin coste" : `Se descuentan 14 créditos de tu saldo (${formatEUR(user?.credits_eur || 0)}) · recálculos gratis`}
                                        </div>
                                    </div>
                                </div>
                            </motion.form>
                            </>
                        ) : (
                            <ResultView
                                key="result"
                                result={result}
                                previousResult={previousResult}
                                initialResult={initialResult}
                                appliedRecs={appliedRecs}
                                isAdmin={isAdmin}
                                user={user}
                                onReset={reset}
                                onEdit={editData}
                                onApplyRecommendation={(adj, id) => applyRecommendation(adj, id)}
                                onApplyAll={applyAllRecommendations}
                            />
                        )}
                    </AnimatePresence>
                </section>
            </main>
            <div className="print:hidden">
                <Footer />
                <WhatsAppFab />
            </div>
        </div>
    );
}

/* ============================================================ */
function formatMiles(v) {
    if (v === "" || v === null || v === undefined) return "";
    let s = String(v).replace(",", ".");
    const isNeg = s.startsWith("-");
    if (isNeg) s = s.slice(1);
    const parts = s.split(".");
    const intPart = (parts[0] || "").replace(/\D/g, "");
    const decPart = parts[1] != null ? parts[1].replace(/\D/g, "") : null;
    const intFmt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return (isNeg ? "-" : "") + intFmt + (decPart != null ? "," + decPart : "");
}
function parseMiles(s) {
    if (!s) return "";
    return String(s).replace(/\./g, "").replace(",", ".");
}

function Field({ label, value, onChange, testId, hint, example }) {
    const [showTip, setShowTip] = React.useState(false);
    return (
        <div>
            <label className="flex items-start gap-1.5 text-[11px] tracking-wider uppercase text-[var(--gf-text-secondary)] mb-1.5 leading-tight">
                <span className="flex-1">{label}</span>
                {(hint || example) && (
                    <span className="relative flex-shrink-0">
                        <button
                            type="button"
                            onMouseEnter={() => setShowTip(true)}
                            onMouseLeave={() => setShowTip(false)}
                            onFocus={() => setShowTip(true)}
                            onBlur={() => setShowTip(false)}
                            onClick={(e) => { e.preventDefault(); setShowTip((v) => !v); }}
                            className="w-4 h-4 rounded-full bg-[var(--gf-gold)]/15 text-[var(--gf-gold)] text-[10px] font-bold hover:bg-[var(--gf-gold)]/30 transition-colors flex items-center justify-center normal-case"
                            aria-label="Ayuda"
                            data-testid={`${testId}-help`}
                        >
                            ?
                        </button>
                        {showTip && (
                            <div className="absolute z-30 right-0 top-full mt-1 w-64 p-3 bg-[var(--gf-navy)] text-white text-[11px] leading-relaxed normal-case tracking-normal shadow-lg" role="tooltip">
                                {hint && <p className="mb-1.5">{hint}</p>}
                                {example && (
                                    <p className="text-[10px] text-[var(--gf-gold)] mt-1 pt-1 border-t border-white/15">
                                        <span className="uppercase tracking-widest text-[9px]">Ejemplo</span> · <span className="mono">{example}</span>
                                    </p>
                                )}
                            </div>
                        )}
                    </span>
                )}
            </label>
            <div className="relative">
                <input
                    type="text"
                    inputMode="decimal"
                    className="gf-input mono pr-8 text-sm"
                    value={formatMiles(value)}
                    onChange={(e) => onChange(parseMiles(e.target.value))}
                    placeholder="0"
                    data-testid={testId}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[var(--gf-text-muted)] pointer-events-none">€</span>
            </div>
        </div>
    );
}

function MiniTotal({ label, value, accent }) {
    // Colores más fuertes: Total Activo (gold) y Total PN+Pasivo (navy) resaltados con fondo.
    // Extra accents: emerald (rdo cuadra) / rose (rdo NO cuadra).
    const styles = {
        gold: "bg-[var(--gf-gold)] text-white border-[var(--gf-gold)]",
        navy: "bg-[var(--gf-navy)] text-white border-[var(--gf-navy)]",
        emerald: "bg-emerald-600 text-white border-emerald-600",
        rose: "bg-rose-600 text-white border-rose-600",
    };
    const cls = styles[accent] || styles.gold;
    return (
        <div className={`border-l-4 ${cls} px-3 py-2.5 shadow-sm`}>
            <div className="text-[10px] tracking-widest uppercase opacity-85">{label}</div>
            <div className="mono font-bold text-base mt-0.5">{formatEUR(value || 0)}</div>
        </div>
    );
}

function Bullet({ icon, children }) {
    return (
        <div className="flex items-start gap-2 text-sm text-white/80">
            <span className="text-[var(--gf-gold)] mt-0.5">{icon}</span>
            <span>{children}</span>
        </div>
    );
}

function TextField({ label, value, onChange, testId, type = "text", placeholder }) {
    return (
        <div>
            <label className="block text-[11px] tracking-wider uppercase text-[var(--gf-text-secondary)] mb-1.5 leading-tight">
                {label}
            </label>
            <input
                type={type}
                className="gf-input text-sm w-full"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testId}
                autoComplete="off"
            />
        </div>
    );
}

function BlockedView({ onLogin, onQuickRecharge, recharging, user }) {
    const PRICE = 14;
    const saldo = Number(user?.credits_eur || 0);
    return (
        <motion.div
            key="blocked"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto"
            data-testid="tm-blocked"
        >
            <div className="border-l-4 border-l-[var(--gf-gold)] bg-[var(--gf-navy)] text-white text-center py-12 px-6 md:px-10 shadow-lg" data-testid="tm-blocked-card">
                <div className="inline-flex w-16 h-16 rounded-full bg-[var(--gf-gold)]/15 items-center justify-center mb-5">
                    <Lock size={32} weight="fill" className="text-[var(--gf-gold)]" />
                </div>
                <div className="micro-label text-[var(--gf-gold)] mb-3">— Créditos insuficientes</div>
                <h2 className="serif text-4xl md:text-5xl mb-5 leading-[1.05] font-medium">
                    Te faltan <span className="italic text-[var(--gf-gold)]">créditos</span>
                </h2>
                <p className="text-white/80 leading-relaxed max-w-xl mx-auto mb-6">
                    El Dictamen Pre-Bancario cuesta <strong>{PRICE} créditos</strong> y tu saldo actual es de{" "}
                    <strong className="text-[var(--gf-gold)]" data-testid="tm-blocked-saldo">{formatEUR(saldo)}</strong>.
                    Recarga tu monedero (1 € = 1 crédito) y vuelve: tus cifras siguen guardadas en el borrador.
                </p>

                <div className="inline-flex items-baseline gap-2 mt-2">
                    <span className="serif text-6xl text-[var(--gf-gold)] font-medium">{PRICE}</span>
                    <span className="text-xs uppercase tracking-widest text-white/70 font-semibold">créditos</span>
                </div>
                <div className="text-sm text-white/70 mb-8 mono mt-1">
                    <strong className="text-white">{PRICE} € + IVA</strong> · recálculos sobre el mismo dictamen incluidos
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-lg mx-auto">
                    <button onClick={onQuickRecharge} disabled={recharging} className="btn-gold justify-center flex-1 disabled:opacity-60" data-testid="tm-blocked-quick-recharge">
                        <Lightning size={16} weight="fill" /> {recharging ? "Abriendo pago seguro…" : "Recargar 14 créditos · 16,94 €"}
                    </button>
                    <Link to="/pagos" className="btn-outline justify-center text-white border-white/40 hover:bg-white/10 flex-1" data-testid="tm-blocked-register">
                        Otras recargas <ArrowRight size={16} />
                    </Link>
                </div>
                <button onClick={onLogin} className="mt-4 text-xs text-white/60 hover:text-white underline underline-offset-4" data-testid="tm-blocked-login">
                    Volver al formulario
                </button>

                <div className="text-[11px] text-white/60 mt-6 leading-relaxed max-w-md mx-auto">
                    9 ratios bancarios · veredicto verde / ámbar / rojo · alertas críticas · recomendaciones contables legales ·
                    simulador <em>Antes / Después</em> · PDF profesional
                </div>
            </div>
        </motion.div>
    );
}

/* ============================================================ */
/*  ResultView                                                   */
/* ============================================================ */
function ResultView({ result, previousResult, initialResult, appliedRecs, isAdmin, user, onReset, onEdit, onApplyRecommendation, onApplyAll }) {
    const v = result.veredicto;
    const isLocked = !!result.locked && !isAdmin;
    const veredictoBg = {
        verde: "bg-gradient-to-br from-green-50 to-emerald-50 border-l-[var(--gf-success)] text-[var(--gf-success)]",
        ambar: "bg-gradient-to-br from-amber-50 to-orange-50 border-l-[var(--gf-warning)] text-[var(--gf-warning)]",
        rojo: "bg-gradient-to-br from-red-50 to-rose-50 border-l-[var(--gf-danger)] text-[var(--gf-danger)]",
    };
    const veredictoIcons = {
        verde: <ShieldCheck size={48} weight="fill" />,
        ambar: <Warning size={48} weight="fill" />,
        rojo: <XCircle size={48} weight="fill" />,
    };

    return (
        <motion.div
            id="resultado-maquina"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="tm-result"
        >
            {/* Cabecera impresa: solo visible en PDF */}
            <div className="hidden print:flex justify-between items-end mb-6 pb-4 border-b border-[var(--gf-border)]">
                <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--gf-gold)] font-semibold mb-1">
                        Gestión Fiscal 347 · La Máquina de la Verdad
                    </div>
                    <h1 className="serif text-3xl text-[var(--gf-navy)]">Dictamen pre-bancario</h1>
                </div>
                <div className="text-[10px] mono text-[var(--gf-text-muted)] text-right">
                    Generado el {new Date().toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" })}
                </div>
            </div>

            {/* Barra de progreso de optimización (si hay baseline previo) */}
            {initialResult && appliedRecs && appliedRecs.size > 0 && (
                <ProgressBar initial={initialResult} current={result} applied={appliedRecs.size} />
            )}

            {/* Barra de acciones — Imprimir / Exportar PDF + Email (oculto en modo bloqueado) */}
            <div className="flex flex-col md:flex-row justify-end gap-3 mb-4 print:hidden" data-testid="tm-actions-bar">
                {!isLocked && <EmailCapture result={result} />}
                {!isLocked && (
                <button
                    onClick={() => window.print()}
                    className="btn-outline text-sm"
                    data-testid="tm-print-btn"
                >
                    <Printer size={14} weight="light" /> Imprimir / Exportar a PDF
                </button>
                )}
            </div>

            {/* AVISO INICIAL — datos aportados por el cliente */}
            <div className="border-l-2 border-[var(--gf-gold)] bg-[var(--gf-cream)]/60 px-5 py-3 mb-6 flex items-start gap-3" data-testid="tm-disclaimer-top">
                <Info size={16} weight="bold" className="text-[var(--gf-gold)] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[var(--gf-text-secondary)] leading-relaxed">
                    <strong className="text-[var(--gf-navy)]">Aviso previo:</strong> el presente diagnóstico se elabora
                    <strong> exclusivamente con los datos aportados por el cliente</strong>. Cualquier corrección que se proponga
                    será aplicada únicamente si el cliente la valida expresamente — la decisión final es siempre suya.
                </p>
            </div>

            {/* VEREDICTO */}
            <div className={`border-l-4 p-8 mb-8 ${veredictoBg[v.color]}`}>
                <div className="flex flex-col md:flex-row items-start gap-6">
                    <div className="flex-shrink-0">{veredictoIcons[v.color]}</div>
                    <div className="flex-1">
                        <div className="micro-label mb-2 text-[var(--gf-text-muted)]">— Veredicto de la Máquina de la Verdad</div>
                        <h2 className="serif text-3xl md:text-4xl mb-3 text-[var(--gf-navy)]">{v.titulo}</h2>
                        <p className="text-base text-[var(--gf-text-secondary)] leading-relaxed">{v.mensaje}</p>
                        <div className="mt-4 flex flex-wrap gap-4 text-xs">
                            <div className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full" style={{ background: "var(--gf-success)" }} />
                                <span className="mono">{v.verdes_ratios}/9 ratios saludables</span>
                            </div>
                            {v.ambars > 0 && (
                                <div className="flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full" style={{ background: "var(--gf-warning)" }} />
                                    <span className="mono">{v.ambars} alertas ámbar</span>
                                </div>
                            )}
                            {v.rojos > 0 && (
                                <div className="flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full" style={{ background: "var(--gf-danger)" }} />
                                    <span className="mono">{v.rojos} alertas críticas</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                {/* COL 1-2 */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Action bar — Modificar datos · solo admin en modo público */}
                    {!isLocked && (
                    <div className="gf-card flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-l-4 border-l-[var(--gf-gold)] print:hidden" data-testid="tm-edit-bar">
                        <div className="flex items-start gap-3">
                            <PencilSimple size={22} weight="light" className="text-[var(--gf-gold)] flex-shrink-0 mt-0.5" />
                            <div>
                                <div className="font-semibold text-sm text-[var(--gf-navy)] mb-0.5">¿Y si tocas algún dato?</div>
                                <p className="text-xs text-[var(--gf-text-secondary)] leading-relaxed">
                                    Vuelve al formulario con tus datos cargados, corrige lo que veas mal y la Máquina recalculará el diagnóstico — comparándolo con el actual ("Antes / Después").
                                </p>
                            </div>
                        </div>
                        <button onClick={onEdit} className="btn-gold whitespace-nowrap" data-testid="tm-edit-btn">
                            <PencilSimple size={14} weight="bold" /> Modificar datos
                        </button>
                    </div>
                    )}

                    {/* CTA bloqueo público — pago para desbloquear todo */}
                    {isLocked && (
                        <div className="gf-card border-l-4 border-l-[var(--gf-gold)] bg-[var(--gf-navy)] text-white print:hidden" data-testid="tm-locked-cta">
                            <div className="flex items-start gap-4">
                                <Lock size={32} weight="fill" className="text-[var(--gf-gold)] flex-shrink-0 mt-1" />
                                <div className="flex-1">
                                    <div className="micro-label text-[var(--gf-gold)] mb-1">— Diagnóstico preliminar gratuito</div>
                                    <h3 className="serif text-2xl mb-2">Hemos detectado <strong className="text-[var(--gf-gold)]">3 ratios con problemas</strong> en tu balance</h3>
                                    <p className="text-sm text-white/80 leading-relaxed mb-4">
                                        Te mostramos <strong className="text-white">2 de los 9 ratios</strong> que aplica un analista bancario. Los demás están en zona crítica o ámbar — el banco los marcará.
                                        Desbloquea el <strong className="text-[var(--gf-gold)]">Dictamen Pre-Bancario completo</strong> con los 9 ratios, alertas, recomendaciones contables legales y simulador "Antes / Después".
                                    </p>
                                    <div className="flex items-baseline gap-2 mb-3">
                                        <span className="serif text-3xl text-[var(--gf-gold)]">24€</span>
                                        <span className="text-[10px] uppercase tracking-widest text-white/70 font-semibold">+ IVA</span>
                                        <span className="text-xs text-white/60 mono ml-2">· 29,04€ IVA incluido</span>
                                    </div>
                                    <button
                                        onClick={async () => {
                                            try { await api.post("/public/leads/upgrade-click", { email: user?.email || null, telefono: user?.telefono || null, source: "locked_cta" }); } catch { /* opcional */ }
                                            window.location.href = user?.email
                                                ? "/pagos"
                                                : "/registro?next=/pagos";
                                        }}
                                        className="btn-gold"
                                        data-testid="tm-locked-cta-btn"
                                    >
                                        Recargar créditos y desbloquear <ArrowRight size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* RATIOS */}
                    <div className="gf-card">
                        <div className="flex items-center justify-between mb-2">
                            <div className="micro-label">— Diagnóstico técnico</div>
                            {previousResult && (
                                <span className="text-[10px] tracking-widest uppercase text-[var(--gf-gold)] font-semibold" data-testid="tm-vs-badge">vs intento anterior</span>
                            )}
                        </div>
                        <h3 className="serif text-2xl mb-4">9 ratios bancarios clave</h3>
                        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="tm-ratios">
                            {Object.entries(result.ratios).map(([key, r]) => (
                                <RatioCard key={key} ratio={r} previous={previousResult?.ratios?.[key]} />
                            ))}
                        </div>
                    </div>

                    {/* ALERTAS */}
                    {result.alertas && result.alertas.length > 0 && (
                        <div className="gf-card">
                            <div className="micro-label mb-2">— Lo que el banco verá</div>
                            <h3 className="serif text-2xl mb-4">{result.alertas.length} alerta{result.alertas.length > 1 ? "s" : ""} detectada{result.alertas.length > 1 ? "s" : ""}</h3>
                            <div className="space-y-3" data-testid="tm-alertas">
                                {result.alertas.map((a, i) => <AlertBlock key={i} a={a} />)}
                            </div>
                        </div>
                    )}

                    {/* RECOMENDACIONES DE OPTIMIZACIÓN */}
                    {result.recomendaciones && result.recomendaciones.length > 0 && (
                        <div className="gf-card border-t-4 border-t-[var(--gf-gold)]">
                            <div className="flex items-start gap-3 mb-4">
                                <Lightbulb size={24} weight="fill" className="text-[var(--gf-gold)] flex-shrink-0 mt-1" />
                                <div className="flex-1">
                                    <div className="micro-label">— Recomendaciones de optimización</div>
                                    <h3 className="serif text-2xl mt-1">Cómo podríamos darle la vuelta</h3>
                                    <p className="text-sm text-[var(--gf-text-secondary)] mt-2 leading-relaxed">
                                        Tu cliente decide si las aplica. Cada corrección es una técnica contable legal — pulsa
                                        <strong className="text-[var(--gf-navy)]"> "Aplicar y recalcular"</strong> para ver el efecto al instante.
                                    </p>
                                </div>
                                {result.recomendaciones.length > 1 && (
                                    <button
                                        onClick={onApplyAll}
                                        className="btn-gold text-xs whitespace-nowrap print:hidden"
                                        data-testid="tm-apply-all-btn"
                                        title="Aplica de golpe las correcciones propuestas"
                                    >
                                        <Sparkle size={12} weight="fill" /> Aplicar todas
                                    </button>
                                )}
                            </div>
                            <div className="space-y-4" data-testid="tm-recomendaciones">
                                {result.recomendaciones.map((r, i) => (
                                    <RecommendationBlock
                                        key={r.id}
                                        rec={r}
                                        idx={i + 1}
                                        onApply={() => onApplyRecommendation(r.ajuste_propuesto, r.id)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* CASCADA P&G */}
                    {result.magnitudes_pl && (
                    <div className="gf-card">
                        <div className="micro-label mb-2">— Cuenta de explotación</div>
                        <h3 className="serif text-2xl mb-4">Cascada P&G</h3>
                        <PLCascade m={result.magnitudes_pl} />
                    </div>
                    )}
                </div>

                {/* COL 3: Balance recap + Upsell */}
                <div className="space-y-4">
                    {!isLocked && <BalanceRecap totales={result.totales} pl={result.magnitudes_pl} />}

                    <div className="gf-card print:hidden" style={{ background: "var(--gf-navy)", color: "white" }}>
                        <div className="flex items-start gap-3 mb-4">
                            <Sparkle size={24} weight="fill" className="text-[var(--gf-gold)] mt-1" />
                            <div>
                                <h3 className="serif text-xl mb-1">Siguiente paso</h3>
                                <p className="text-sm text-white/75 leading-relaxed">
                                    Este dictamen queda guardado en tu <strong className="text-[var(--gf-gold)]">historial</strong> y puedes reabrirlo o recalcularlo cuando quieras sin coste.
                                    Si vas a presentar expediente al banco, genera el <strong>Informe Financiero con IA</strong> (24 créditos) para entregarlo.
                                </p>
                            </div>
                        </div>
                        <Link to="/analisis/nuevo" className="btn-gold w-full justify-center" data-testid="tm-cta-informe">
                            Generar Informe Financiero <ArrowRight size={16} />
                        </Link>
                        <Link to="/dashboard" className="block text-center text-xs text-white/60 hover:text-white underline underline-offset-4 mt-3" data-testid="tm-cta-dashboard">
                            Ver mi historial de dictámenes
                        </Link>
                        <button
                            onClick={onReset}
                            className="block w-full text-center text-xs text-white/60 hover:text-white underline underline-offset-4 mt-3"
                            data-testid="tm-reset"
                        >
                            Nuevo dictamen con otros datos · 14 créditos
                        </button>
                    </div>

                    <div className="text-[10px] text-[var(--gf-text-muted)] italic px-2">
                        {result.metodologia}
                    </div>
                </div>
            </div>

            {/* DISCLAIMER FORMAL DE CIERRE */}
            <div className="mt-10 border-t border-[var(--gf-border)] pt-6" data-testid="tm-disclaimer-bottom">
                {/* Línea comercial — sólo visible al imprimir */}
                <div className="hidden print:block mb-4 text-center text-sm text-[var(--gf-navy)] border border-[var(--gf-gold)] py-3 px-4">
                    Para el dictamen completo con plan de acción detallado, contacte con
                    <strong className="text-[var(--gf-gold)]"> Gestión Fiscal 347</strong> ·
                    WhatsApp <span className="mono">+34 604 080 483</span> · info@gf347.es ·
                    <span className="mono">gf347.es</span>
                </div>

                <div className="bg-[var(--gf-subtle)] border border-[var(--gf-border)] p-5">
                    <div className="micro-label mb-2 text-[var(--gf-text-muted)]">— Consideración legal</div>
                    <p className="text-xs text-[var(--gf-text-secondary)] leading-relaxed">
                        El presente <strong className="text-[var(--gf-navy)]">diagnóstico pre-bancario</strong> se ha elaborado
                        exclusivamente a partir de la información económico-financiera <strong>aportada por el cliente</strong>,
                        sin verificación contable, fiscal ni documental por parte de Gestión Fiscal 347. Las recomendaciones
                        de optimización propuestas constituyen técnicas contables legales aplicables al PGC español; su
                        aplicación efectiva queda <strong>a la libre decisión del cliente</strong>, quien asume la responsabilidad
                        de validar la procedencia, el soporte documental y el encaje fiscal de cada ajuste contable.
                        Este informe no sustituye al dictamen formal de un auditor de cuentas ni a una revisión profesional
                        completa, y se emite con carácter orientativo para uso interno del cliente.
                    </p>
                    <p className="text-[10px] text-[var(--gf-text-muted)] mt-3 mono">
                        Gestión Fiscal 347 · Generado el {new Date().toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" })}
                    </p>
                </div>
            </div>
        </motion.div>
    );
}

function RatioCard({ ratio, previous }) {
    function fmt(r) {
        if (!r || r.value === null || r.value === undefined || Number.isNaN(r.value)) return "—";
        if (r.kind === "pct") return `${formatNum(r.value * 100, 1)}%`;
        if (r.kind === "eur") return formatEUR(r.value);
        return formatNum(r.value, 2);
    }
    // Ratio bloqueado (público sin pagar): mostramos un candado y CTA
    if (ratio.locked) {
        return (
            <div className="border border-[var(--gf-border)] p-3 relative bg-[var(--gf-navy)]/[0.03] flex items-center gap-3" data-testid={`tm-ratio-locked-${(ratio.label || "x").toLowerCase().replace(/[^a-z]/g, "-")}`}>
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[var(--gf-navy)]/10 flex-shrink-0">
                    <Lock size={16} weight="fill" className="text-[var(--gf-navy)]/60" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="micro-label text-[10px] leading-tight truncate">{ratio.label}</div>
                    <div className="text-[10px] text-[var(--gf-text-muted)] mt-0.5">Desbloquea por 29,04€ IVA incluido · Informe completo</div>
                </div>
            </div>
        );
    }
    const order = { rojo: 0, ambar: 1, "n/a": 1, verde: 2 };
    const improved = previous && order[ratio.level] > order[previous.level];
    const worsened = previous && order[ratio.level] < order[previous.level];
    return (
        <div className="border border-[var(--gf-border)] p-3 relative" data-testid={`tm-ratio-${ratio.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <div className="flex items-center justify-between mb-2">
                <span className="micro-label text-[10px] leading-tight">{ratio.label}</span>
                {improved && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-[var(--gf-success)]" title="Ha mejorado">↑ mejora</span>
                )}
                {worsened && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-[var(--gf-danger)]" title="Ha empeorado">↓ empeora</span>
                )}
            </div>
            <div className="flex items-end justify-between gap-2">
                <div className="mono font-semibold text-xl leading-none" style={{ color: COLORS[ratio.level] }}>
                    {fmt(ratio)}
                </div>
                <div className="text-right">
                    <span className="inline-block text-[9px] tracking-widest uppercase font-semibold" style={{ color: COLORS[ratio.level] }}>
                        {BADGE[ratio.level]}
                    </span>
                    <div className="text-[9px] mono text-[var(--gf-text-muted)] mt-0.5">óptimo {ratio.optimal}</div>
                </div>
            </div>
            {previous && (improved || worsened || previous.value !== ratio.value) && (
                <div className="text-[9px] mono text-[var(--gf-text-muted)] mt-1.5 pt-1.5 border-t border-[var(--gf-border)]">
                    Antes: <span style={{ color: COLORS[previous.level] }}>{fmt(previous)}</span>
                </div>
            )}
        </div>
    );
}

function AlertBlock({ a }) {
    const styles = {
        rojo: { border: "border-l-[var(--gf-danger)]", bg: "bg-red-50", icon: <XCircle size={18} weight="fill" className="text-[var(--gf-danger)]" /> },
        ambar: { border: "border-l-[var(--gf-warning)]", bg: "bg-amber-50", icon: <Warning size={18} weight="fill" className="text-[var(--gf-warning)]" /> },
    };
    const s = styles[a.severity] || styles.ambar;
    return (
        <div className={`border-l-4 p-4 ${s.border} ${s.bg}`}>
            <div className="flex items-start gap-3">
                <div className="mt-0.5">{s.icon}</div>
                <div className="flex-1">
                    <div className="font-semibold text-sm text-[var(--gf-navy)] mb-1">{a.titulo}</div>
                    <p className="text-sm text-[var(--gf-text-secondary)] leading-relaxed">{a.detalle}</p>
                </div>
            </div>
        </div>
    );
}

function RecommendationBlock({ rec, idx, onApply }) {
    const [confirming, setConfirming] = React.useState(false);
    return (
        <div className="border border-[var(--gf-border)] bg-[var(--gf-cream)]/30 p-4" data-testid={`tm-rec-${rec.id}`}>
            <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[var(--gf-gold)] text-white flex items-center justify-center font-semibold text-sm mono">
                    {idx}
                </div>
                <div className="flex-1">
                    <div className="font-semibold text-base text-[var(--gf-navy)] mb-1">{rec.titulo}</div>
                    <div className="text-[10px] tracking-widest uppercase text-[var(--gf-gold)] font-semibold mb-2">{rec.norma}</div>
                    <p className="text-sm text-[var(--gf-text-secondary)] leading-relaxed mb-2">
                        <strong className="text-[var(--gf-navy)]">Diagnóstico: </strong>{rec.diagnostico}
                    </p>
                    <p className="text-sm text-[var(--gf-text-secondary)] leading-relaxed mb-3">
                        <strong className="text-[var(--gf-navy)]">Acción contable: </strong>{rec.accion}
                    </p>
                    {rec.impacto && (
                        <div className="text-xs text-[var(--gf-success)] mb-2 border-l-2 border-[var(--gf-success)] pl-3 py-1 bg-green-50/60">
                            <strong>Impacto previsto:</strong> {rec.impacto}
                        </div>
                    )}
                    {rec.warning && (
                        <div className="text-xs text-[var(--gf-warning)] mb-3 border-l-2 border-[var(--gf-warning)] pl-3 py-1 bg-amber-50">
                            <strong>⚠️ Atención:</strong> {rec.warning}
                        </div>
                    )}

                    {!confirming ? (
                        <button
                            onClick={() => setConfirming(true)}
                            className="btn-gold mt-2 print:hidden"
                            data-testid={`tm-rec-apply-${rec.id}`}
                        >
                            <Sparkle size={14} weight="fill" /> Aplicar y recalcular
                        </button>
                    ) : (
                        <div className="mt-3 border-t border-[var(--gf-border)] pt-3 print:hidden">
                            <div className="text-xs text-[var(--gf-navy)] font-semibold mb-2">¿Aplicar esta corrección? Estos serán los nuevos valores:</div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mono mb-3">
                                {Object.entries(rec.ajuste_propuesto).map(([k, v]) => (
                                    <div key={k} className="flex justify-between">
                                        <span className="text-[var(--gf-text-secondary)] text-[11px]">{labelOf(k)}</span>
                                        <span className="font-semibold text-[var(--gf-navy)]">{formatEUR(v)}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <button onClick={onApply} className="btn-gold flex-1 justify-center text-sm" data-testid={`tm-rec-confirm-${rec.id}`}>
                                    Sí, aplicar
                                </button>
                                <button onClick={() => setConfirming(false)} className="btn-outline text-sm" data-testid={`tm-rec-cancel-${rec.id}`}>
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Sólo visible al imprimir: aviso de pendiente de validación */}
                    <div className="hidden print:block mt-3 pt-2 border-t border-[var(--gf-border)] text-xs italic text-[var(--gf-text-muted)]">
                        Pendiente de validación por el cliente. Valores propuestos:
                        <span className="mono"> {Object.entries(rec.ajuste_propuesto).map(([k, v]) => `${labelOf(k)} ${formatEUR(v)}`).join(" · ")}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

function labelOf(k) {
    const map = {
        activo_no_corriente: "Activo no corriente",
        activo_corriente: "Activo corriente",
        capital: "Capital",
        reservas_remanente: "Reservas y remanente",
        resultado_ejercicio: "Resultado del ejercicio",
        pasivo_no_corriente: "Pasivo no corriente",
        pasivo_corriente: "Pasivo corriente",
        coste_personal: "Coste de personal",
        gastos_generales: "Gastos generales",
        ingresos_corrientes: "Ingresos corrientes",
        variacion_obra: "Variación obra",
        compras_aprovisionamientos: "Compras",
        variacion_existencias: "Var. existencias",
        amortizaciones: "Amortizaciones",
        gastos_financieros: "Gastos financieros",
    };
    return map[k] || k;
}

function PLCascade({ m }) {
    const rows = [
        { l: "Ingresos (cifra de negocios + obra)", v: m.ingresos, accent: false },
        { l: "− Consumos (compras − var. existencias)", v: -m.consumos, accent: false },
        { l: "= Margen bruto", v: m.margen_bruto, accent: true },
        { l: "= Valor añadido (tras gastos generales)", v: m.valor_anadido, accent: false },
        { l: "= EBITDA", v: m.ebitda, accent: true },
        { l: "= EBIT (tras amortizaciones)", v: m.ebit, accent: false },
        { l: "= Resultado antes impuestos", v: m.bai, accent: true },
    ];
    return (
        <div className="border border-[var(--gf-border)] divide-y divide-[var(--gf-border)]" data-testid="tm-cascade">
            {rows.map((r, i) => (
                <div key={i} className={`flex items-center justify-between px-4 py-2.5 ${r.accent ? "bg-[var(--gf-cream)]" : ""}`}>
                    <span className={`text-sm ${r.accent ? "font-semibold text-[var(--gf-navy)]" : "text-[var(--gf-text-secondary)]"}`}>{r.l}</span>
                    <span className={`mono font-semibold ${r.v < 0 ? "text-[var(--gf-danger)]" : r.accent ? "text-[var(--gf-navy)]" : "text-[var(--gf-text)]"}`}>{formatEUR(r.v)}</span>
                </div>
            ))}
        </div>
    );
}

function BalanceRecap({ totales: t, pl }) {
    // Coherencia entre el resultado declarado en balance y el BAI calculado
    const rdoBalance = Number(t?.resultado_ejercicio) || 0;
    const bai = Number(pl?.bai) || 0;
    const ref = Math.max(Math.abs(bai), Math.abs(rdoBalance), 1);
    const gap = Math.abs(bai - rdoBalance);
    const gapPct = gap / ref;
    const incoherente = (Math.abs(rdoBalance) > 100 || Math.abs(bai) > 100) && gapPct > 0.15;

    return (
        <>
        <div className="gf-card-ficha" data-testid="tm-balance-recap">
            <div className="micro-label mb-3">— Tus datos introducidos</div>
            <h4 className="serif text-lg mb-3 text-[var(--gf-navy)]">Balance de situación</h4>

            <div className="grid grid-cols-2 gap-2 text-xs mb-4">
                <div>
                    <div className="text-[9px] tracking-widest uppercase text-[var(--gf-gold)] font-semibold mb-1.5 pb-1 border-b border-[var(--gf-gold)]">Activo</div>
                    <Row l="A. no corriente" v={t.activo_no_corriente} />
                    <Row l="A. corriente" v={t.activo_corriente} />
                    <Row l="Total Activo" v={t.total_activo} bold />
                </div>
                <div>
                    <div className="text-[9px] tracking-widest uppercase text-[var(--gf-navy)] font-semibold mb-1.5 pb-1 border-b border-[var(--gf-navy)]">PN + Pasivo</div>
                    <Row l="Capital" v={t.capital} />
                    <Row l="Reservas/Reman." v={t.reservas_remanente} />
                    <Row l="Rdo. ejercicio" v={t.resultado_ejercicio} highlight={incoherente} />
                    <Row l="Pasivo NC" v={t.pasivo_no_corriente} />
                    <Row l="Pasivo C" v={t.pasivo_corriente} />
                    <Row l="Total PN+Pas." v={t.total_pn_pasivo} bold />
                </div>
            </div>

            {/* Comparativa visual Resultado Balance vs BAI calculado P&G */}
            <div className={`text-xs p-3 border-l-2 ${incoherente ? "border-l-[var(--gf-danger)] bg-red-50" : "border-l-[var(--gf-success)] bg-green-50/60"}`}>
                <div className="flex justify-between items-baseline mb-1">
                    <span className="text-[10px] tracking-wider uppercase text-[var(--gf-text-secondary)]">Rdo. Balance</span>
                    <span className="mono font-semibold" style={{ color: incoherente ? "var(--gf-danger)" : "var(--gf-success)" }}>{formatEUR(rdoBalance)}</span>
                </div>
                <div className="flex justify-between items-baseline mb-1">
                    <span className="text-[10px] tracking-wider uppercase text-[var(--gf-text-secondary)]">BAI según P&G</span>
                    <span className="mono font-semibold" style={{ color: incoherente ? "var(--gf-danger)" : "var(--gf-success)" }}>{formatEUR(bai)}</span>
                </div>
                <div className={`text-[10px] mt-1.5 pt-1.5 border-t ${incoherente ? "border-[var(--gf-danger)]/30 text-[var(--gf-danger)]" : "border-[var(--gf-success)]/30 text-[var(--gf-success)]"} font-semibold`}>
                    {incoherente ? `⚠️ Diferencia ${formatEUR(gap)} (${(gapPct*100).toFixed(0)}%) — incoherente` : "✓ Coherentes"}
                </div>
            </div>
        </div>

        {/* Cuenta de Pérdidas y Ganancias · datos introducidos */}
        {pl && (pl.ingresos_corrientes != null) && (
            <div className="gf-card-ficha mt-4" data-testid="tm-pyg-recap">
                <div className="micro-label mb-3">— Tus datos introducidos</div>
                <h4 className="serif text-lg mb-3 text-[var(--gf-navy)]">Cuenta de Pérdidas y Ganancias</h4>
                <div className="text-xs space-y-0.5">
                    <Row l="Ingresos corrientes" v={pl.ingresos_corrientes} />
                    <Row l="Variación obra en curso" v={pl.variacion_obra} />
                    <Row l="Compras y aprovisionam." v={pl.compras_aprovisionamientos} />
                    <Row l="Variación existencias" v={pl.variacion_existencias} />
                    <Row l="Gastos generales" v={pl.gastos_generales} />
                    <Row l="Coste personal + SS" v={pl.coste_personal} />
                    <Row l="Amortizaciones" v={pl.amortizaciones} />
                    <Row l="Gastos financieros" v={pl.gastos_financieros} />
                    <Row l="Resultado antes de impuestos (BAI)" v={pl.bai} bold highlight={incoherente} />
                </div>
            </div>
        )}
        </>
    );
}

function Row({ l, v, bold, highlight }) {
    return (
        <div className={`flex justify-between items-baseline py-0.5 ${bold ? "pt-1 mt-1 border-t border-[var(--gf-border)]" : ""}`}>
            <span className={`text-[11px] ${bold ? "font-semibold text-[var(--gf-navy)]" : "text-[var(--gf-text-secondary)]"}`}>{l}</span>
            <span className={`mono ${bold ? "font-semibold text-[var(--gf-navy)]" : ""} ${highlight ? "text-[var(--gf-danger)] font-semibold" : ""} ${(Number(v) || 0) < 0 ? "text-[var(--gf-danger)]" : ""}`}>{formatEUR(Number(v) || 0)}</span>
        </div>
    );
}

/* ============================================================ */
/*  ProgressBar — visualiza evolución del veredicto              */
/* ============================================================ */
function ProgressBar({ initial, current, applied }) {
    const order = { rojo: 0, ambar: 1, verde: 2 };
    const initLevel = initial.veredicto.color;
    const currLevel = current.veredicto.color;
    const improved = order[currLevel] > order[initLevel];
    const total = (current.recomendaciones?.length || 0) + applied;
    const colors = { rojo: "var(--gf-danger)", ambar: "var(--gf-warning)", verde: "var(--gf-success)" };

    return (
        <div className="bg-white border border-[var(--gf-border)] p-4 mb-6 print:hidden" data-testid="tm-progress-bar">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <Sparkle size={18} weight="fill" className="text-[var(--gf-gold)]" />
                    <div>
                        <div className="text-[10px] tracking-widest uppercase text-[var(--gf-gold)] font-semibold mb-0.5">Progreso de optimización</div>
                        <div className="text-sm text-[var(--gf-navy)] font-semibold">
                            {applied} {applied === 1 ? "corrección aplicada" : "correcciones aplicadas"}
                            {total > applied && <span className="text-[var(--gf-text-muted)] font-normal"> · {total - applied} pendiente{total - applied > 1 ? "s" : ""}</span>}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                    <span className="text-[var(--gf-text-secondary)]">Veredicto:</span>
                    <span className="font-semibold mono uppercase" style={{ color: colors[initLevel] }}>
                        {initial.veredicto.titulo.split(" ")[1] || initLevel}
                    </span>
                    <ArrowRight size={12} className="text-[var(--gf-gold)]" />
                    <span className="font-semibold mono uppercase" style={{ color: colors[currLevel] }}>
                        {current.veredicto.titulo.split(" ")[1] || currLevel}
                    </span>
                    {improved && <Sparkle size={12} weight="fill" className="text-[var(--gf-success)]" />}
                </div>
            </div>
        </div>
    );
}

/* ============================================================ */
/*  EmailCapture — lead magnet                                   */
/* ============================================================ */
function EmailCapture({ result }) {
    const [open, setOpen] = React.useState(false);
    const [email, setEmail] = React.useState("");
    const [nombreEmpresa, setNombreEmpresa] = React.useState("");
    const [sending, setSending] = React.useState(false);
    const [sent, setSent] = React.useState(false);
    const [error, setError] = React.useState("");

    async function handleSend() {
        if (!email || !email.includes("@")) {
            setError("Introduce un email válido");
            return;
        }
        setError(""); setSending(true);
        try {
            await api.post("/public/truth-machine/lead", {
                email,
                nombre_empresa: nombreEmpresa || null,
                veredicto: result.veredicto.color,
                rojos: result.veredicto.rojos || 0,
                ambars: result.veredicto.ambars || 0,
            });
            setSent(true);
        } catch (err) {
            setError(formatApiError(err, "Error al enviar"));
        } finally {
            setSending(false);
        }
    }

    if (sent) {
        return (
            <div className="text-sm text-[var(--gf-success)] flex items-center gap-2" data-testid="tm-email-sent">
                <CheckCircle size={16} weight="fill" /> Te lo hemos enviado a {email}
            </div>
        );
    }

    if (!open) {
        return (
            <button onClick={() => setOpen(true)} className="btn-outline text-sm" data-testid="tm-email-open">
                <Sparkle size={14} weight="fill" /> Recibir por email
            </button>
        );
    }

    return (
        <div className="flex flex-col sm:flex-row items-stretch gap-2 bg-[var(--gf-cream)] border border-[var(--gf-gold)] p-3" data-testid="tm-email-form">
            <input
                type="text"
                placeholder="Empresa (opcional)"
                value={nombreEmpresa}
                onChange={(e) => setNombreEmpresa(e.target.value)}
                className="gf-input text-sm"
                style={{ minWidth: 140 }}
                data-testid="tm-email-empresa"
            />
            <input
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="gf-input text-sm"
                style={{ minWidth: 180 }}
                data-testid="tm-email-input"
            />
            <button
                onClick={handleSend}
                disabled={sending}
                className="btn-gold text-sm whitespace-nowrap"
                data-testid="tm-email-send"
            >
                {sending ? "Enviando…" : "Enviar"} <ArrowRight size={14} />
            </button>
            {error && <div className="text-xs text-[var(--gf-danger)] sm:hidden">{error}</div>}
        </div>
    );
}
